import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Tracks dashboard session state.
 *
 *   { loading, authenticated, authRequired, error, login(password), logout() }
 *
 * - `authRequired=false` means the hub has no DASHBOARD_PASSWORD set; the SPA
 *   skips the login screen entirely. The hub still says authenticated=true
 *   in that mode so `authenticated` ends up true regardless.
 * - `login` throws on failure (so the LoginScreen can show the error inline).
 * - `logout` clears the cookie server-side then flips `authenticated` to false.
 */
export function useAuth() {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [authRequired, setAuthRequired] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setAuthenticated(!!me.authenticated);
      setAuthRequired(!!me.authRequired);
      setError(null);
    } catch (e) {
      setError(e);
      setAuthenticated(false);
      setAuthRequired(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(
    async (password) => {
      await api.login(password);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setAuthenticated(false);
    }
  }, []);

  return { loading, authenticated, authRequired, error, login, logout, refresh };
}
